import { AlertBanner, Text, breakpoints } from "@asurion-core/mx-asurion-ui-react";
import { Grid, Row, SearchResultItemCard } from "@ubif/components";
import { SearchCustomersResponse } from "@ubif/util-store";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { CustomerSearchNoResults } from "./elements/CustomerSearchNoResults";
import { ShowMore } from "./elements/ShowMore";

export const CustomerSearchResults = ({
  customersToDisplay = [],
  searchedCustomersData = [],
  customersCount,
  customerSelectionClick,
  moreClick,
  debouncedSearchCriteria,
  isSearchSuccessOrError,
  showMoreCustomers,
  isShowMoreCustomersPending
}: {
  customersToDisplay: SearchCustomersResponse;
  searchedCustomersData: SearchCustomersResponse;
  customersCount: number;
  customerSelectionClick: (id: string, clientId?: number, customerTypeId?: number) => void;
  moreClick: () => void;
  debouncedSearchCriteria: string;
  isSearchSuccessOrError: boolean;
  showMoreCustomers: boolean;
  isShowMoreCustomersPending: boolean;
}) => {
  const { t } = useTranslation("root-config");

  return (
    <>
      <Text className="page-heading-pb" as="h2" weight="feather">
        {t("GlobalSearch.SearchResultsWithCount", { count: customersCount })}
      </Text>
      {searchedCustomersData?.length > 1 ? (
        <Row>
          <AlertBanner isOpen={true} iconSrc="system-alert-alert" hideDismissButton={true}>
            <Text>{t("GlobalSearch.MultipleCustomersSearchAlertBanner")}</Text>
          </AlertBanner>
        </Row>
      ) : null}
      {customersToDisplay.length > 1 && (
        <>
          <Row>
            <Text as="h3" size={2} weight="feather">
              {t("GlobalSearch.CustomersSearchResultsWithCount", {
                count: customersCount
              })}
            </Text>
          </Row>
          <Row>
            <StyledGrid minWidth={20}>
              {customersToDisplay.map((customer) => (
                <Fragment key={customer.id}>
                  <SearchResultItemCard
                    customerId={customer.id}
                    firstName={customer.firstName}
                    lastName={customer.lastName}
                    fullName={customer.fullName}
                    email={customer.primaryEmail}
                    phone={customer.primaryPhone}
                    canCall={false}
                    canEmail={!!customer.canEmail}
                    canSms={false}
                    clientId={customer?.clientId ?? 1}
                    preferredContactLabel={t("GlobalSearch.ContactPreference")}
                    customerTypeId={customer?.customerTypeId}
                    onClick={customerSelectionClick}
                  />
                </Fragment>
              ))}
            </StyledGrid>
          </Row>
          <ShowMore
            showMoreLabel={t("Showmore")}
            display={!showMoreCustomers}
            placeInCenter={true}
            isPending={isShowMoreCustomersPending}
            onClick={moreClick}
          />
        </>
      )}
      {customersToDisplay.length <= 1 && searchedCustomersData?.length === 0 && isSearchSuccessOrError && (
        <CustomerSearchNoResults searchText={debouncedSearchCriteria} />
      )}
    </>
  );
};

const StyledGrid = styled(Grid)`
  grid-template-columns: repeat(1, minmax(20rem, 1fr));

  ${breakpoints.md2} {
    grid-template-columns: repeat(2, minmax(20rem, 1fr));
  }
  ${breakpoints.lg} {
    grid-template-columns: repeat(3, minmax(20rem, 1fr));
  }
  ${breakpoints.xxl} {
    grid-template-columns: repeat(4, minmax(20rem, 1fr));
  }
`;
